import {
  Actions,
  MultiSelectOption,
  Permission,
  ResultStatus,
  RoleOptions,
  Subjects,
  UserData,
  UsersRole,
} from '@dispatch/shared/types';
import { Ability, MongoQuery } from '@casl/ability';
import {
  AsyncSelect,
  CheckBoxListSkeleton,
  DrawerComponent,
  DrawerMeta,
  Form,
  RenderFuncProps,
  Section,
  SectionDivider,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { indigo } from '@mui/material/colors';
import { format } from 'date-fns';
import { isNull, isEmpty } from 'lodash';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { UserTaskGroups } from '../components/UserTaskGroups';
import { UserPermissions } from '../components/UserPermissions';
import { AnyObject } from 'yup/lib/types';
import { useDebounce } from 'react-use';
import { useGetResourcesPaginated } from '@dispatch/shared/hooks';
import { useQueryClient } from 'react-query';

const DEBOUNCE_DELAY = 1000;
const RESOURCES_PAGE_SIZE = 25;

const schema = yup
  .object({
    name: yup.string().required('Name is required'),
    externalId: yup.string().nullable(),
    email: yup.string().required('Email is required'),
    role: yup
      .object()
      .shape({
        title: yup.string(),
        value: yup.string(),
        data: yup.object(),
      })
      .required('Role is required')
      .nullable(),
  })
  .required();

export type UserForm = {
  externalId?: string;
  resourceId?: MultiSelectOption | '';
  name: string;
  role?: UsersRole | string;
  email: string;
  taskGroups: any;
  permissions: string[];
  disabledPermissions: string[];
};

export type UserDetailsProps = {
  data?: UserData;
  status?: ResultStatus;
  onSave: (value: UserForm) => void;
  onDeactivate?: (value: UserData) => void;
  onActivate?: (value: UserData) => void;
  onResetUser?: (value: UserData) => void;
  taskGroupOptions: { title: string; value: string }[];
  roleOptions: RoleOptions[] | undefined;
  rolePermissions: Permission[];
  rolePermissionStatus: ResultStatus;
  usersRole?: UsersRole;
  setUsersRole: React.Dispatch<React.SetStateAction<UsersRole | undefined>>;
  pullPermissions: boolean;
  setPullPermissions: React.Dispatch<React.SetStateAction<boolean>>;
  permissions?: Ability<[Actions, Subjects], MongoQuery<AnyObject>>;
  associatedResource?: MultiSelectOption;
  setAssociatedResource?: React.Dispatch<
    React.SetStateAction<MultiSelectOption | undefined>
  >;
};

export function UserDetails({
  data,
  onSave,
  onDeactivate,
  onActivate,
  onResetUser,
  status,
  taskGroupOptions,
  roleOptions,
  rolePermissions,
  rolePermissionStatus,
  usersRole,
  setUsersRole,
  pullPermissions,
  setPullPermissions,
  permissions,
  associatedResource,
  setAssociatedResource,
}: UserDetailsProps) {
  const params = useParams();
  const userId = params['userId'];
  const navigate = useNavigate();
  const [resetData, setResetData] = React.useState<boolean>(false);
  const [userTaskGroups, setUserTaskGroups] = React.useState<any>({});

  const hasResetUserPermissions = permissions?.can('reset', 'users');

  React.useEffect(() => {
    if (
      data?.taskGroups === undefined ||
      Object.keys(userTaskGroups).length !== 0
    )
      return;
    setUserTaskGroups({ ...data?.taskGroups });
  }, [data?.taskGroups]);

  const [userPermissions, setUserPermissions] = React.useState<
    Record<string, Permission>
  >({});
  const [disabledPermissions, setDisabledPermissions] = React.useState<
    Record<string, Permission>
  >({});
  const [roleApplied, setRoleApplied] = React.useState<boolean>(false);

  const formDefaults: UserForm = {
    name: '',
    role: '',
    email: '',
    taskGroups: {},
    permissions: [],
    disabledPermissions: [],
    resourceId: '',
  };

  const {
    control: formControl,
    formState: { errors: formErrors, isDirty },
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { ...formDefaults },
  });

  const handleDeactivation = () => {
    if (!data) return;
    onDeactivate?.(data);
  };

  const handleActivation = () => {
    if (!data) return;
    onActivate?.(data);
  };

  const handleResetUser = () => {
    if (!data) return;
    onResetUser?.(data);
  };

  const handleSave = () => {
    handleSubmit(onSave)();

    if (userId) {
      reset({ ...getValues() });
      return setResetData(true);
    }
  };

  const renderTaskGroupss = () => {
    return (
      <UserTaskGroups
        taskGroupOptions={taskGroupOptions}
        userTaskGroups={userTaskGroups}
        setUserTaskGroups={setUserTaskGroups}
        setValue={setValue}
      />
    );
  };

  const handleUserPermissions = React.useCallback(
    (currentUserPermissions: Permission[], disabled: string[] | undefined) => {
      if (disabled) {
        setValue('disabledPermissions', disabled, { shouldDirty: false });
      }

      currentUserPermissions?.forEach((permission: Permission) => {
        const currentPermissions = userPermissions;
        const keyVal = permission.operationId;

        currentPermissions[keyVal] = permission;
        setUserPermissions(currentPermissions);

        if (disabled && disabled.includes(permission?.id)) {
          const disabledPermissionObj = disabledPermissions;
          if (!disabledPermissionObj[permission.operationId]) {
            disabledPermissionObj[permission.operationId] = permission;
            setDisabledPermissions(disabledPermissionObj);
          }
        }

        setValue(
          'permissions',
          Object.entries(currentPermissions).flatMap(
            (task: [string, Permission]) => {
              if (!disabled?.includes(task[1].id)) {
                return task[1].id;
              } else {
                return [];
              }
            }
          ),
          {
            shouldDirty: false,
          }
        );
      });
    },
    [setValue, userPermissions]
  );

  const applyRole = React.useCallback(
    (role: UsersRole) => {
      setUserPermissions({});
      setRoleApplied(true);
      if (data?.role?.id !== role.value) {
        setPullPermissions(true);
        if (rolePermissions) {
          handleUserPermissions(
            role?.data?.permissions as Permission[],
            data?.disabledPermissions
          );
        }
      }
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    [handleUserPermissions, rolePermissions]
  );

  React.useEffect(() => {
    if (rolePermissions && pullPermissions) {
      setPullPermissions(false);
      handleUserPermissions(rolePermissions, data?.disabledPermissions);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [rolePermissions, pullPermissions, handleUserPermissions]);

  React.useEffect(() => {
    setValue('disabledPermissions', []);
    setValue('permissions', []);
    const role: RoleOptions[] | undefined = roleOptions?.filter(
      (option: RoleOptions) => option.value === data?.role?.id
    );

    if (data) {
      const setData: UserForm = {
        externalId: data?.externalId,
        resourceId: associatedResource,
        name: `${data?.firstName} ${data?.lastName || ''}` || '',
        email: data?.email || '',
        permissions: [],
        disabledPermissions: [],
        taskGroups: [],
        role: '',
      };

      if (role && role?.length > 0) {
        setUsersRole(role[0] as UsersRole);
        applyRole(role[0] as UsersRole);

        setData.role = role[0] as UsersRole;
      }

      reset(setData);
      handleUserPermissions(data?.role?.permissions, data?.disabledPermissions);
    } else {
      reset({
        ...formDefaults,
      });
    }

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [data, roleOptions, setUsersRole, associatedResource]);

  React.useEffect(() => {
    if (resetData) {
      setResetData(false);
    }
  }, [resetData]);

  const renderRolePermissions = React.useMemo<Permission[]>(() => {
    return rolePermissions.length > 0
      ? rolePermissions
      : data?.role?.permissions ?? [];
  }, [rolePermissions, data?.role?.permissions]);

  const renderPermissions = () => {
    return (
      <UserPermissions
        rolePermissions={renderRolePermissions}
        userPermissions={userPermissions}
        disabledPermissions={disabledPermissions}
        setUserPermissions={setUserPermissions}
        setDisabledPermissions={setDisabledPermissions}
        setValue={setValue}
        disabledIds={watch('disabledPermissions')}
      />
    );
  };

  const [resourceInput, setResourceInput] = React.useState('');
  const [hasCleared, setHasCleared] = React.useState(false);
  const [debouncedResourceInput, setDebouncedResourceInput] =
    React.useState('');
  useDebounce(() => setDebouncedResourceInput(resourceInput), DEBOUNCE_DELAY, [
    resourceInput,
  ]);

  React.useEffect(() => {
    if (hasCleared) {
      setHasCleared(false);
    }
  }, [debouncedResourceInput]);
  const filteredResources = useGetResourcesPaginated({
    filters: {
      resourceName: debouncedResourceInput,
      isActive: true,
      pageSize: RESOURCES_PAGE_SIZE,
    },
    enabled:
      !!debouncedResourceInput &&
      !hasCleared &&
      resourceInput !== associatedResource?.title,
  });

  const mappedFilteredResources = React.useMemo(() => {
    if (filteredResources.status === 'success' && filteredResources.data) {
      return filteredResources.data.map(
        (resource): MultiSelectOption => ({
          title: resource.name,
          value: resource.integrationGUID,
        })
      );
    }
    return []; // Return an empty array as the default value
  }, [filteredResources.data, filteredResources.status]);

  const queryClient = useQueryClient();
  const setRole = watch('role');
  const formArgs = {
    render: ({ control, errors }: RenderFuncProps) => {
      return (
        <>
          <Section title={`User Information`}>
            <Grid container spacing={2} marginBottom={2}>
              <Grid size={6}>
                <TextField
                  id="name"
                  name="name"
                  label="User Name"
                  required
                  control={control}
                  errors={errors}
                  sx={{ width: '100%' }}
                />
              </Grid>
              <Grid data-test-id="resourceId" size={6}>
                <AsyncSelect
                  control={control}
                  onValueChange={(value) => {
                    if (isNull(value)) {
                      setValue('resourceId', '');
                    }
                  }}
                  errors={errors}
                  id="resourceId"
                  name="resourceId"
                  label="Associated Resource"
                  loading={filteredResources?.status === 'loading'}
                  options={mappedFilteredResources || []}
                  tagLimit={10}
                  setInput={setResourceInput}
                  data-testid="associated-resource-field"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="baseline">
              <Grid data-test-id="role" size={6}>
                <SelectField
                  id={`role`}
                  name={`role`}
                  label="Role"
                  required
                  control={control}
                  errors={errors}
                  onValueChange={(value) => {
                    setRoleApplied(false);
                    setUserPermissions({});
                    if (value) {
                      setUsersRole(value as UsersRole);
                      setValue('role', value as UsersRole);
                    }
                  }}
                  error={errors['roles']?.message ?? null}
                  options={roleOptions || []}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  id="email"
                  name="email"
                  label="User Email"
                  required
                  control={control}
                  errors={errors}
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid size={12}>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '0.9375rem',
                    lineHeight: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    letterSpacing: '0.4px',
                    color: 'rgba(0, 0, 0, 0.54)',
                    padding: '0.625rem 0',
                  }}
                >
                  Apply role to update User Rights below
                </Typography>
              </Grid>
            </Grid>
            <Grid container>
              <Grid data-test-id="apply-role" size={6}>
                <Button
                  disabled={!setRole}
                  variant="contained"
                  sx={{
                    backgroundColor: indigo[500],
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    lineHeight: '22px',
                    letterSpacing: '0.46px',
                  }}
                  onClick={() => {
                    if (usersRole) {
                      applyRole(usersRole);
                    }
                  }}
                >
                  APPLY ROLE
                </Button>
              </Grid>
              {hasResetUserPermissions && (
                <Grid data-test-id="reset-sso-user" size={6}>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: indigo[500],
                      fontWeight: 700,
                      fontSize: '0.8125rem',
                      lineHeight: '22px',
                      letterSpacing: '0.46px',
                    }}
                    onClick={() => {
                      handleResetUser();
                    }}
                  >
                    Reset SSO User
                  </Button>
                </Grid>
              )}
            </Grid>
          </Section>
          <SectionDivider />
          <Grid container>
            <Grid size={6}>
              <Stack>
                <p style={{ margin: 0 }}>User Rights</p>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '0.9375rem',
                    lineHeight: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    letterSpacing: '0.4px',
                    color: 'rgba(0, 0, 0, 0.54)',
                    padding: '0.625rem 0',
                  }}
                >
                  User rights will change based on role
                </Typography>
                {rolePermissionStatus === 'loading' ? (
                  <CheckBoxListSkeleton />
                ) : null}
                {roleApplied &&
                  rolePermissionStatus !== 'loading' &&
                  renderPermissions()}
              </Stack>
            </Grid>
            <Grid size={6}>
              <Stack>
                <p style={{ margin: 0 }}>Task Groups</p>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: '0.9375rem',
                    lineHeight: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    letterSpacing: '0.4px',
                    color: 'rgba(0, 0, 0, 0.54)',
                    padding: '0.625rem 0',
                  }}
                >
                  Add this user to available task groups
                </Typography>
                {renderTaskGroupss()}
              </Stack>
            </Grid>
          </Grid>
        </>
      );
    },
  };

  const renderTitle = () => {
    if (data?.firstName) {
      return data?.firstName + ' ' + (data?.lastName || '');
    }

    if (userId) {
      return 'User';
    }

    return 'Add a New User';
  };

  return (
    <DrawerComponent
      status={status}
      showDeactivateButton={isNull(data?.deactivatedAt) && !isEmpty(data)}
      onDeactivate={handleDeactivation}
      showActivateButton={!isNull(data?.deactivatedAt) && !isEmpty(data)}
      onActivate={handleActivation}
      onSave={handleSave}
      saveDisabled={!isDirty}
      showButtons={permissions?.can?.('put', 'users')}
      onClose={() => navigate('/admin/users')}
      title={renderTitle()}
      subtitle={'User'}
      sx={{ width: '55vw' }}
      showIsActive={!!data}
      isActive={isNull(data?.deactivatedAt)}
      metas={[
        <DrawerMeta
          title="Created On"
          subtitle={
            data?.createdAt
              ? format(new Date(data?.createdAt), 'MM/dd/yyyy')
              : format(new Date(), 'MM/dd/yyyy')
          }
        />,
      ]}
    >
      <Form<UserForm>
        {...formArgs}
        errors={formErrors}
        control={formControl}
        handleSubmit={handleSubmit}
      />
    </DrawerComponent>
  );
}
